import { RENDERCARDTYPE } from "@/constants/renderCardType";
import useAppStore from "@/context/store";
import { getSearch } from "@/tracker/shared/global";
import { buildSearchParams } from "@/utils/gotoTopicSearch";
import useListenPinsPopstate from "@/utils/hooks/useListenPinsPopstate";
import { usePinQueue } from "@/utils/hooks/usePinQueue";
import imageUrl from "@/utils/imageUrl";
import { useMemoizedFn, useUpdateEffect } from "ahooks";
import { isBoolean } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EVENT } from "../../constants/event";
import RecommendMiddle from "../../pageComponents/Search/RecommendMiddle";
import RecommendTopicList from "../../pageComponents/Search/RecommendTopicList";
import AiGameplayCard from "../AiGameplayCard";
import BoardCardA from "../BoardCardA";
import FoldCard from "../FoldCard";
import HBList from "../HBList";
import MasonrySkeleton from "../Masonry/skeleton";
import PinCardSkeleton from "../PinCard/Skeleton";
import BoardSwiper from "../PinDetail/BoardSwiper";
import SameBoardModule from "../PinDetail/SameBoardModule";
import SameBoardOperationCard from "../PinDetail/SameBoardOperationCard";
import PurePinCard from "../PurePinCard";
import QuestionaireCard from "../QuestionnaireCard";
import RecommendCard from "../RecommendCard";
import {
  RecommendSearchWordsCardA,
  RecommendSearchWordsCardB,
} from "../RecommendSearchWords";
import SimilarCard from "../SimilarCard";
import SimilarCardA from "../SimilarCard/CardA";
import SimilarMaterialList from "../SimilarMaterialList";

/**
 * @param {{
 * hideUser?: boolean;
 * userVisible?: boolean;
 * hideCount?: boolean;
 * dataSource?: Array<any>;
 * showBoardLink?: boolean;
 * showMaterialTag?: boolean;
 * disableModal?: boolean;
 * disableQueue?: any;
 * isModal?: boolean;
 * hasMore?: any;
 * onLoadMore?: any;
 * disableFile?: any;
 * showDownload?: boolean;
 * isSearch?: boolean;
 * searchWord?: string;
 * scrollableTarget?: string;
 * masonryKey?: string;
 * customColumnNum?: number
 * customColumnSize?: number
 * masonryOptions?: any;
 * pageSize?: number;
 * coverPinQueue?: boolean;
 * loginGuide?: boolean;
 * hasPinCreated?: boolean;
 * }}
 */
export default function PinList({
  hideUser,
  userVisible,
  hideCount,
  dataSource,
  showBoardLink,
  showMaterialTag = true,
  disableModal,
  disableQueue,
  isModal,
  disableFile, // 是否点击详情的时候把文件带过去
  showDownload,
  isSearch,
  searchWord,
  extraPinQueue,
  customColumnNum,
  customColumnSize,
  masonryOptions = {},
  coverPinQueue = false,
  loginGuide = true,
  hasPinCreated = false,
  showLikeBtn,
  moduleName,
  flagValue, // 新增：服务端AB实验值
  currentBoard, // 新增：当前画板信息，用于画板选择器默认值
  stackIndex = 0, // 新增：弹窗堆栈索引
  enableCurrentBoardPriority, // 新增：是否启用当前画板优先逻辑
  ...props
}) {
  const {
    user,
    pinOpenType,
    column: columnNum,
    showPinDesc,
    showPinUserInfo,
    columnSize: globalColumnSize,
  } = useAppStore(
    ({
      user,
      pinOpenType,
      column,
      showPinDesc,
      showPinUserInfo,
      columnSize,
    }) => ({
      user,
      pinOpenType,
      column,
      showPinDesc,
      showPinUserInfo,
      columnSize,
    })
  );

  // 获取搜索相关数据
  const searchData = getSearch();
  // 构建跳转参数
  const searchParams = useMemo(() => {
    return buildSearchParams(searchWord, { searchData });
  }, [searchWord, searchData]);

  const masonryRef = useRef(null);
  const columnSize = customColumnSize || globalColumnSize;
  const [list, setList] = useState(dataSource);
  const deleteMapRef = useRef({});
  const isLogin = !!user;

  useEffect(() => {
    setList(dataSource);
  }, [dataSource]);

  useUpdateEffect(() => {
    masonryRef.current?.relayout();
  }, [showPinDesc, showPinUserInfo]);

  const filter = useCallback((dataSource) => {
    return dataSource.filter((record) => {
      if (!record?.pin_id) {
        return false;
      }
      if (deleteMapRef.current[record.pin_id]) {
        return false;
      }
      if (record.file?.file_id) {
        return record.file_id === record.file.file_id;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    const onDelete = (evt) => {
      const pinIds = Array.isArray(evt.detail) ? evt.detail : [evt.detail];
      pinIds.forEach((pinId) => {
        deleteMapRef.current[pinId] = true;
      });

      setList((list) => {
        return list.filter((pin) => !pinIds.includes(pin.pin_id));
      });
    };
    window.addEventListener(EVENT.DELETE_PIN, onDelete);
    return () => {
      window.removeEventListener(EVENT.DELETE_PIN, onDelete);
    };
  }, []);

  const searchInfo = useMemo(() => {
    if (!isSearch) {
      return null;
    }
    const distCodes = [];
    const fileIds = [];
    let count = 0;
    dataSource?.forEach((item) => {
      if (item && item?.ext?.dist_code && item?.file_id && count < 120) {
        count++;
        distCodes.push(item?.ext?.dist_code);
        fileIds.push(item?.file_id);
      }
    }, []);

    return { distCodes, fileIds };
  }, [isSearch, dataSource]);

  const render = useMemoizedFn((record, index) => {
    // 单个骨架屏渲染
    if (record.type === RENDERCARDTYPE.pinCardSkeleton) {
      return (
        <PinCardSkeleton
          key={record.pin_id}
          pin_id={record.pin_id}
          style={{
            width: columnSize * record.mergeCol + 16 * (record.mergeCol - 1),
            ...record.style,
          }}
        />
      );
    }
    // pin 详情卡片
    if (record.type === RENDERCARDTYPE.pinDetailInfo) {
      if (props.renderDetailInfo) {
        return props.renderDetailInfo(record);
      }
    }
    // AI 灵感推荐卡片
    if (record.type === RENDERCARDTYPE.aiGameplay) {
      if (props.renderAiGameplay) {
        return props.renderAiGameplay(record);
      }
      return (
        <AiGameplayCard
          key={record.pin_id}
          record={record}
          location={record.location || 0}
          actualLocation={record.actualLocation || index + 1}
          columnSize={columnSize}
        />
      );
    }
    // 推荐搜索词
    if (record.type === RENDERCARDTYPE.recommendSearchWordsA) {
      return (
        <RecommendSearchWordsCardA
          key={record.pin_id}
          record={record}
          columnSize={columnSize}
          moduleLocation={index + 1}
        />
      );
    }
    if (record.type === RENDERCARDTYPE.recommendSearchWordsB) {
      return (
        <RecommendSearchWordsCardB
          key={record.pin_id}
          record={record}
          columnSize={columnSize}
          columnNum={record.mergeCol}
          moduleLocation={index + 1}
        />
      );
    }
    // 同在画板运营位
    if (record.type === RENDERCARDTYPE.boardOperation) {
      return (
        <SameBoardOperationCard
          key={`boardOperation-${stackIndex}-${record.pinId}`}
          record={record}
          columnSize={columnSize}
          customColumnNum={record?.mergeCol || customColumnNum}
          stackIndex={stackIndex}
        />
      );
    }
    // 同在画板新模组
    if (record.type === RENDERCARDTYPE.sameBoardModule) {
      return (
        <SameBoardModule
          key={`sameBoardModule-${stackIndex}-${record.pinId}`}
          boards={record.boards || record.boardsData}
          pinId={record.pinId}
          onLoadMore={record.onLoadMore}
          hasMore={record.hasMore}
          loading={record.loading}
          searchWord={searchWord}
          stackIndex={stackIndex}
          style={{
            width: columnSize * record.mergeCol + 16 * (record.mergeCol - 1),
          }}
        />
      );
    }

    if (record.type === RENDERCARDTYPE.recommendMiddle) {
      return (
        <RecommendMiddle
          key={record.pin_id}
          searchWord={searchWord}
          topics={record}
          columnNum={record.mergeCol}
        />
      );
    }
    // 推荐专题列表
    if (record.type === RENDERCARDTYPE.recommendTopicList) {
      return (
        <RecommendTopicList
          key={record.pin_id}
          searchWord={searchWord}
          topics={record}
          recommendTopicListColNum={record.mergeCol}
          columnSize={columnSize}
          isFromMasonry={true}
        />
      );
    }
    // 广告
    if (record.type === RENDERCARDTYPE.recommend) {
      return (
        <RecommendCard
          key={record.pin_id}
          record={record}
          columnSize={columnSize}
        />
      );
    }
    // 问卷
    if (record.type === RENDERCARDTYPE.questionaire) {
      return (
        <QuestionaireCard key={record.pin_id} record={record} {...searchInfo} />
      );
    }
    // 折叠
    if (record.folding_num && record.folding_num > 0) {
      return (
        <FoldCard key={record.pin_id} record={record} columnSize={columnSize} />
      );
    }

    // 相似推荐
    if (record.type === RENDERCARDTYPE.similar) {
      return (
        <SimilarCard
          key={record.pinId}
          pins={record?.data?.pins}
          href={`/pins/${record.pinId}/similar/materials`}
          isSimilar
          title="素材专区"
          subTitle="相似商用素材"
          data-gd-click="button_click"
          data-button-name="更多相似商用素材"
          target="_blank"
          data-gd-expose="resource_loc_expose"
          data-resource-type="图片"
          data-resource-id="0"
          data-resource-title="相似商用素材"
          data-content-source="相似商用素材_为你推荐"
          loading={record.data.loading}
          width={columnSize}
        ></SimilarCard>
      );
    }

    // 相似商用素材（使用SimilarMaterialList组件）
    if (record.type === RENDERCARDTYPE.similarMaterial) {
      // 根据全局列数动态计算内部瀑布流列数
      const globalColumnNum = customColumnNum || columnNum;
      const col = globalColumnNum < 6 ? 4 : 5;

      // 根据全局列数动态计算高度
      let dynamicHeight;
      if (globalColumnNum < 6) {
        dynamicHeight = 368;
      } else if (globalColumnNum >= 6 && globalColumnNum < 9) {
        dynamicHeight = 448;
      } else {
        dynamicHeight = 634;
      }

      // 计算宽度时确保使用最新的 columnSize
      const calculatedWidth =
        (record.mergeCol * columnSize +
          16 * (record.mergeCol - 1) -
          34 -
          4 * (col - 1)) /
        col;
      return (
        <SimilarMaterialList
          key={`${record.pin_id}-${globalColumnNum}-${col}-${columnSize}`} // 添加 columnSize 到 key 中，确保宽度变化时重新渲染
          pin_id={record.pinId}
          similiarList={record.similarMaterialList}
          searchWord={searchWord}
          columnNum={col}
          width={calculatedWidth}
          gutter={4}
          threshold={800}
          showSub={false}
          isFull={true}
          stackIndex={stackIndex} // 传递弹窗堆栈索引
          style={{
            width: columnSize * record.mergeCol + 16 * (record.mergeCol - 1),
            // 根据全局列数动态计算高度，覆盖isFull样式
            height: `${dynamicHeight}px`,
            // 通过CSS变量传递高度，确保内部容器也使用正确的高度
            "--similar-height": `${dynamicHeight}px`,
          }}
          onSelect={record.onSelect}
        />
      );
    }

    // 单画板卡片（插入到瀑布流中）
    if (record.type === RENDERCARDTYPE.boardCardA) {
      return (
        <BoardCardA
          key={record.pin_id}
          id={record.pin_id}
          board={record.board}
          width={columnSize}
          viewSource={record.viewSource}
          currentPin={record.currentPin}
          location={record.location}
          searchWord={searchWord}
        />
      );
    }

    // 推荐画板横向滚动卡片（固定在详情卡片下方）
    if (record.type === RENDERCARDTYPE.boardSwiper) {
      return (
        <BoardSwiper
          key={record.pin_id}
          boardsData={record.boardsData || []}
          title={record.title || "推荐画板"}
          pinId={record.pinId}
          currentPin={record.currentPin}
          searchWord={searchWord}
          onShowMore={record.onShowMore}
          onLoadMore={record.onLoadMore}
          useNewApi={record.useNewApi}
          getNextPage={record.getNextPage}
          stackIndex={stackIndex}
          data-module-name="推荐画板"
          style={{
            width: columnSize * record.mergeCol + 16 * (record.mergeCol - 1),
          }}
        />
      );
    }

    // AI灵感推荐
    if (record.type === RENDERCARDTYPE.artRecommend) {
      const file = record.data.file;
      const fileId = record.data.fileId;
      const fileWidth = file.width;
      const fileHeight = file.height;
      const fileUrl = encodeURIComponent(imageUrl(file, { width: fileWidth }));

      return (
        <SimilarCard
          key={record.pinId}
          width={columnSize}
          record={record}
          style={{
            marginBottom: "15px",
          }}
          height={(columnSize * 0.67 + 73) * 2}
          pins={record?.data?.pins}
          href={`https://www.gaoding.art/search?image=${fileUrl}&width=${fileWidth}&height=${fileHeight}&file_id=${fileId}`}
          isAi
          title="免费 AI 创意"
          subTitle="更多 AI 创意素材"
          data-gd-click="button_click"
          data-button-name="更多AI内容推荐"
          target="_blank"
          data-gd-expose="resource_loc_expose"
          data-resource-type="图片"
          data-resource-id="0"
          data-resource-title="更多AI内容推荐"
          data-content-source="AI内容推荐_为你推荐"
        ></SimilarCard>
      );
    }

    // 专题合集
    if (record.type === RENDERCARDTYPE.topicCollection) {
      return (
        <SimilarCard
          key={record.id}
          id={"topicCollection-" + record.id}
          pins={record.pins}
          href={`/topics/topic-${record.urlname}-${record.filter_id}`}
          title={record.content_title || record.title}
          subTitle="源文件下载"
          target="_blank"
          data-gd-expose="topic_expose"
          data-gd-click="topic_click"
          data-topic-id={record.filter_id}
          data-topic-title={record.title}
          data-content-source="信息流_主题合集"
          width={columnSize}
        ></SimilarCard>
      );
    }

    if (record.type === RENDERCARDTYPE.topicCollectionA) {
      return (
        <SimilarCardA
          key={record.id}
          id={"topicCollection-" + record.id}
          pins={record.pins}
          href={`/topics/topic-${record.urlname}-${record.filter_id}${
            searchParams ? `?${searchParams}` : ""
          }`}
          title={record.content_title || record.title}
          subTitle="源文件下载"
          target="_blank"
          data-gd-expose="resource_loc_expose"
          data-gd-click="resource_loc_click"
          data-resource-id={record.filter_id}
          data-resource-title={record.title}
          data-resource-type="二级主题"
          data-resource-source={`信息流_主题合集_${record.title}`}
          data-resource-location={index + 1}
          data-module-name="信息流主题合集"
          data-subject-name="素材主题资源位"
          data-search-word={searchWord}
          data-search-result-number={searchData?.search_result_number || 0}
          width={columnSize}
        />
      );
    }

    // 是否隐藏用户信息：有配置时根据配置走，没有配置时根据登录状态走：已登录隐藏，未登录显示
    // 如果配置了userVisible，则优先使用userVisible
    const isHideUser = isBoolean(userVisible)
      ? !userVisible
      : hideUser || (isBoolean(showPinUserInfo) ? !showPinUserInfo : isLogin);

    return (
      <PurePinCard
        width={columnSize}
        user={user}
        pinOpenType={pinOpenType}
        key={record.pin_id}
        record={record}
        isModal={isModal}
        showBoardLink={showBoardLink}
        disableModal={disableModal}
        disableFile={disableFile}
        hideUser={isHideUser}
        imgLoading={index < 20 ? "eager" : "lazy"}
        hideCount={hideCount}
        showMaterialTag={showMaterialTag}
        showDownload={showDownload}
        showDesc={isBoolean(showPinDesc) ? showPinDesc : !isLogin}
        showSeries={true}
        hasPinCreated={hasPinCreated}
        showLikeBtn={showLikeBtn}
        searchWord={searchWord}
        moduleName={moduleName}
        currentBoard={currentBoard}
        enableCurrentBoardPriority={enableCurrentBoardPriority}
      />
    );
  });

  const filterPins = useMemoizedFn((item) => {
    if (item.type === RENDERCARDTYPE.recommend) {
      return false;
    }

    if (deleteMapRef.current[item.pin_id]) {
      return false;
    }
    if (item.file?.file_id) {
      return item.file_id === item.file.file_id;
    }
    return true;
  });

  usePinQueue(list, extraPinQueue, filterPins, coverPinQueue, disableQueue);

  const isHideUser =
    hideUser || (isBoolean(showPinUserInfo) ? !showPinUserInfo : !isLogin);

  // 解决状态变更时，瀑布流没有重排导致遮挡问题
  useUpdateEffect(() => {
    window.dispatchEvent(new CustomEvent(EVENT.MASONRY_RELAYOUT));
  }, [isHideUser]);

  useListenPinsPopstate();
  return (
    <HBList
      {...props}
      loginGuide={loginGuide}
      uniqueKey="pin_id"
      dataSource={list}
      filter={filter}
      masonryRef={masonryRef}
      flagValue={flagValue} // 传递服务端AB实验值
      masonryOptions={{
        columnNum: customColumnNum || columnNum,
        brickId: "pin_id",
        masonryKey: props.masonryKey,
        columnSize,
        ...masonryOptions,
      }}
      render={render}
      // 首屏使用骨架屏渲染展示 - 全屏详情页实验下不使用firstLoadingContent
      {...(flagValue?.hbPinDetailFeedFlag
        ? {}
        : {
            firstLoadingContent: (
              <MasonrySkeleton columnNum={customColumnNum || columnNum} />
            ),
          })}
    />
  );
}
